import { useMemo, useState, type FormEvent } from 'react';
import { Check, Settings2 } from 'lucide-react';

import type { RecursoTag, Tag, TagResumo } from '@/api/tags';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import {
  escopoPadrao,
  useAplicarTags,
  useEscoposDasTags,
  useEscritoriosDasTags,
  useGestaoTags,
  useTags,
  useTituloTagsPessoais,
} from '@/hooks/useTags';
import type { CacheDeTags } from '@/hooks/useTagsNoCache';
import { agruparPorEscopo } from '@/lib/tags/agrupar';
import { CORES_TAG, corValida } from '@/lib/tags/cores';
import { cn } from '@/lib/utils';

/** As cores do atalho de criar: as seis primeiras da paleta (o diálogo completo tem todas). */
const CORES_RAPIDAS = CORES_TAG.slice(0, 6);
const NOME_MAX = 50;

const resumo = (tag: Tag): TagResumo => ({ id: tag.id, nome: tag.nome, cor: tag.cor, categoria_id: tag.categoria_id });

interface TagsRapidasProps {
  recurso: RecursoTag;
  registroId: string;
  /** As tags do registro agora. */
  aplicadas: TagResumo[];
  noCache: CacheDeTags;
  /** "Gerenciar tags…": fecha o popup e abre o diálogo completo. */
  onGerenciar: () => void;
  /** Tag criada pelo atalho e aplicada: o popup fecha (é um atalho, não uma tela). */
  onCriada: () => void;
}

/**
 * O conteúdo do popup de tags: um clique marca ou desmarca, e um atalho cria uma tag já
 * aplicada — do escritório, para quem é de um (os colegas a veem), senão pessoal; o
 * mesmo "Quem vê" que o diálogo completo já traz marcado. Categoria, outro escopo, editar
 * e excluir ficam no diálogo completo, em "Gerenciar tags…".
 *
 * Monta só com o popup aberto — a lista de mil processos não carrega mil formulários.
 */
export function TagsRapidas({ recurso, registroId, aplicadas, noCache, onGerenciar, onCriada }: TagsRapidasProps) {
  const { tags, categorias, equipes, isLoading } = useTags();
  const escritorios = useEscritoriosDasTags();
  const tituloPessoal = useTituloTagsPessoais();
  const { isOnline } = useOnlineStatus();
  const { criarTag } = useGestaoTags();
  const { definir } = useAplicarTags(recurso, noCache);
  const escritorioDaNova = escopoPadrao(useEscoposDasTags());
  const [nome, setNome] = useState('');
  const [cor, setCor] = useState(CORES_RAPIDAS[0].hex);

  const marcadas = useMemo(() => new Set(aplicadas.map((t) => t.id)), [aplicadas]);
  const secoes = useMemo(
    () =>
      agruparPorEscopo(tags, categorias, escritorios, { tituloPessoal, equipes }).filter((s) => s.grupos.length > 0),
    [tags, categorias, escritorios, tituloPessoal, equipes],
  );

  const alternar = (tag: Tag) => {
    const novas = marcadas.has(tag.id) ? aplicadas.filter((t) => t.id !== tag.id) : [...aplicadas, resumo(tag)];
    void definir(registroId, novas, aplicadas);
  };

  const criar = async (e: FormEvent) => {
    e.preventDefault();
    const limpo = nome.trim();
    if (!limpo) return;
    try {
      const nova = await criarTag.mutateAsync({ nome: limpo, cor, escritorio_id: escritorioDaNova });
      setNome('');
      void definir(registroId, [...aplicadas, resumo(nova)], aplicadas);
      onCriada();
    } catch {
      // O erro já foi mostrado; o nome fica no campo para corrigir.
    }
  };

  return (
    <div className="flex max-h-[70vh] flex-col">
      <p className="px-3 pb-1 pt-2 text-xs font-semibold text-muted-foreground">Tags</p>

      <div className="min-h-0 flex-1 overflow-y-auto px-1">
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Spinner size={16} label="Carregando tags" />
          </div>
        ) : tags.length === 0 ? (
          <p className="px-2 py-2 text-xs text-muted-foreground">Nenhuma tag ainda. Crie a primeira abaixo.</p>
        ) : (
          secoes.map((secao) => (
            <div key={secao.chave}>
              {secoes.length > 1 && (
                <p className="px-2 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {secao.titulo}
                </p>
              )}
              {secao.grupos.map((grupo) => (
                <div key={grupo.categoria?.id ?? 'sem'}>
                  {(grupo.categoria || secao.grupos.length > 1) && (
                    <p className="px-2 pt-1 text-[11px] text-muted-foreground">{grupo.categoria?.nome ?? 'Sem categoria'}</p>
                  )}
                  {grupo.tags.map((tag) => {
                    const marcada = marcadas.has(tag.id);
                    return (
                      <Button
                        key={tag.id}
                        variant="ghost"
                        role="checkbox"
                        aria-checked={marcada}
                        onClick={() => alternar(tag)}
                        className="h-11 w-full justify-start gap-2 px-2 font-normal sm:h-8"
                      >
                        <span
                          className="h-3 w-3 shrink-0 rounded-full border border-black/20"
                          style={{ backgroundColor: corValida(tag.cor) }}
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1 truncate text-left">{tag.nome}</span>
                        {marcada && <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden />}
                      </Button>
                    );
                  })}
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      <form onSubmit={criar} className="space-y-2 border-t px-3 py-2">
        <div className="flex gap-2">
          <Input
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            maxLength={NOME_MAX}
            placeholder={escritorioDaNova ? 'Nova tag do escritório' : 'Nova tag'}
            aria-label="Nome da nova tag"
            disabled={!isOnline}
            className="h-11 sm:h-8"
          />
          <Button type="submit" size="sm" disabled={!isOnline || !nome.trim() || criarTag.isPending} className="h-11 sm:h-8">
            {criarTag.isPending && <Spinner size={16} className="mr-1 text-inherit" />}
            Criar
          </Button>
        </div>
        <div className="flex gap-1.5" role="radiogroup" aria-label="Cor da nova tag">
          {CORES_RAPIDAS.map((opcao) => (
            <Button
              key={opcao.hex}
              type="button"
              variant="ghost"
              size="icon"
              role="radio"
              aria-checked={cor === opcao.hex}
              aria-label={opcao.nome}
              disabled={!isOnline}
              onClick={() => setCor(opcao.hex)}
              className={cn(
                'h-9 w-9 rounded-full border border-black/15 p-0 hover:opacity-90 sm:h-6 sm:w-6',
                cor === opcao.hex && 'ring-2 ring-ring ring-offset-2 ring-offset-background',
              )}
              style={{ backgroundColor: opcao.hex }}
            />
          ))}
        </div>
        {!isOnline && <p className="text-[11px] text-muted-foreground">Sem conexão: dá para marcar e desmarcar; criar volta com a conexão.</p>}
      </form>

      <div className="border-t p-1">
        <Button variant="ghost" size="sm" onClick={onGerenciar} className="h-11 w-full justify-start px-2 text-muted-foreground sm:h-8">
          <Settings2 className="mr-2 h-4 w-4" />
          Gerenciar tags…
        </Button>
      </div>
    </div>
  );
}
